function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useState } from 'react';
import { CustomModal, Button, TableList, Page, Spinner, PageErrorState, PageContentHeader } from 'seagull';
import { useEventReporter } from "../../../event-reporter";
import { useOnMount } from "../../../hooks/useOnMount";
import { modalSize } from "../../../infra/modals";
import { sendErrReport } from "../../../infra/utils";
import { selectors } from "../../../store/state-selectors";
import { useStore, useStoreActions } from "../../../store/store-context";
import { getAccountingDetails } from "../accounting.utils";
import styles from "./MapAccountsModal.module.scss";
import { columnDefs, headerItems } from "./MapAccountsModal.utils";
import { MapAccountsRow } from "./MapAccountsRow";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
export function MapAccountsModal(_ref) {
  let {
    dismissModal,
    accounting
  } = _ref;
  const {
    api,
    logBI,
    fetchIntegrations
  } = useStoreActions();
  const {
    reportToHeap
  } = useEventReporter();
  const accountingIntegration = useStore(selectors.integration.accounting(accounting).integration);
  const [modalState, setModalState] = useState('loading');
  const [accountingAccounts, setAccountingAccounts] = useState();
  const userInfo = useStore(state => state.userInfo);
  const {
    testIdPrefix,
    biCodes
  } = getAccountingDetails(accounting);
  async function fetchAccounts() {
    setModalState('loading');
    try {
      const data = await api.accounting.getAccounts({
        accounting
      });
      setAccountingAccounts(data);
      setModalState('valid');
    } catch (err) {
      setModalState('error');
    }
  }
  async function _onLink(linkType, account) {
    const isPaymentAccount = linkType !== 'expense';
    const biEventCode = isPaymentAccount ? biCodes.linkAccountToPaymentAccount : biCodes.linkAccount;
    logBI(biEventCode, {
      vendorId: userInfo === null || userInfo === void 0 ? void 0 : userInfo.businessId(),
      userId: userInfo === null || userInfo === void 0 ? void 0 : userInfo.id()
    });
    const heapEventName = isPaymentAccount ? "".concat(testIdPrefix, "-select-payments-account") : "".concat(testIdPrefix, "-select-anchor-fees-account");
    reportToHeap('integrations', 'click', heapEventName);
    try {
      await api.accounting.linkAccount({
        accounting,
        data: {
          linkType,
          integratedId: account === null || account === void 0 ? void 0 : account.id
        }
      });
      await fetchIntegrations();
    } catch (error) {
      sendErrReport('Failed to link service template', error);
      throw error;
    }
  }
  useOnMount(() => {
    fetchAccounts();
  });
  return /*#__PURE__*/_jsx(CustomModal, _objectSpread(_objectSpread({
    testId: "".concat(testIdPrefix, "-map-accounts-modal"),
    onClose: dismissModal
  }, modalSize.large), {}, {
    footer: /*#__PURE__*/_jsx("div", {
      className: styles.footer,
      children: /*#__PURE__*/_jsx(Button, {
        onClick: dismissModal,
        testId: "".concat(testIdPrefix, "-map-accounts-close"),
        children: "Done"
      })
    }),
    children: /*#__PURE__*/_jsx(TableList.Provider, {
      columnDefs: columnDefs,
      children: /*#__PURE__*/_jsxs(Page, {
        height: "100%",
        header: "".concat(getAccountingDetails(accounting).fullName, " Accounts"),
        subheader: "Choose accounts to map payments and fees.",
        minWidth: "880px",
        skin: "secondary",
        children: [modalState === 'loading' && /*#__PURE__*/_jsx(LoadingState, {}), modalState === 'error' && /*#__PURE__*/_jsx(ErrorState, {
          onRefresh: fetchAccounts
        }), modalState === 'valid' && accountingAccounts && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(PageContentHeader, {
            children: /*#__PURE__*/_jsx(TableList.Header, {
              items: headerItems
            })
          }), /*#__PURE__*/_jsx(MapAccountsRow, {
            anchorAccount: "Bank account for deposited funds",
            tooltip: "Funds that have cleared and been deposited into your bank account.",
            selectedAccountId: accountingIntegration === null || accountingIntegration === void 0 ? void 0 : accountingIntegration.getBankAccountId(),
            accounts: accountingAccounts.bank,
            onLink: account => _onLink('bank', account),
            testId: "".concat(testIdPrefix, "-map-bank-account-row"),
            noOptionsText: "No Savings or Checking accounts to show",
            accounting: accounting
          }), /*#__PURE__*/_jsx(MapAccountsRow, {
            anchorAccount: "Expense account for merchant fees",
            tooltip: "Anchor fees and credit card fees (if not paid by clients). Anchor recommends using a dedicated account.",
            selectedAccountId: accountingIntegration === null || accountingIntegration === void 0 ? void 0 : accountingIntegration.getExpenseAccountId(),
            accounts: accountingAccounts.expense,
            onLink: account => _onLink('expense', account),
            testId: "".concat(testIdPrefix, "-map-expense-account-row"),
            accounting: accounting
          }), /*#__PURE__*/_jsx(MapAccountsRow, {
            anchorAccount: "Clearing account for collected funds",
            tooltip: "Funds collected and not yet deposited into your bank account.",
            selectedAccountId: accountingIntegration === null || accountingIntegration === void 0 ? void 0 : accountingIntegration.getClearingAccountId(),
            accounts: accountingAccounts.clearing,
            onLink: account => _onLink('clearing', account),
            testId: "".concat(testIdPrefix, "-map-clearing-account-row"),
            accounting: accounting
          })]
        })]
      })
    })
  }));
}
const LoadingState = () => /*#__PURE__*/_jsx("div", {
  className: styles.loading,
  children: /*#__PURE__*/_jsx(Spinner, {
    size: 40,
    testId: "map-accounts-loading"
  })
});
const ErrorState = _ref2 => {
  let {
    onRefresh
  } = _ref2;
  return /*#__PURE__*/_jsx(PageErrorState, {
    testId: "map-accounts-error-state",
    message: "Failed to retrieve the relevant data, please try again later",
    onRefresh: onRefresh,
    skin: "secondary"
  });
};